import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { HtmlPreview } from '@/components/ui/custom/dataDisplay/HtmlPreview';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import useCourseEditing from '@/data/hook/useCourseEditing';
import { deleteBook } from '@/data/services/bookServices';
import { updateLesson } from '@/data/services/lessonServices';
import chapterFormatter from '@/functions/chapterFormatter';
import { isPulished } from '@/functions/handleCourseStatusIcon';
import Book, { bookType } from '@/models/Book';
import Chapter from '@/models/Chapter';
import CoursePath from '@/models/CoursePath';
import { ApiError } from '@/models/Errors';
import Lesson from '@/models/Lesson';
import { buildMaterialUrl } from '@/utils/book/bookUrl';
import { anOldChapter, stringToStringArray } from '@/utils/stringToStringArray';
import alert from '@/utils/shared/alert';
import { Chapters } from './EditMaterialPage/Chapters';
import { onEditBookFn } from './Material.hooks';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

interface ViewMaterialProps {
  book: Book;
  course?: CoursePath;
  lesson: Lesson;
  readOnly?: boolean;
  bookType: bookType;
  updateLessons?: () => Promise<void>;
  onEditBook: onEditBookFn;
  blockId?: number;
  isEditing?: boolean;
}

export default function ViewMaterial({
  book,
  course,
  lesson,
  bookType,
  updateLessons,
  onEditBook,
  blockId,
  isEditing: isLoadingEditingBook,
  readOnly,
}: ViewMaterialProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const navigate = useNavigate();

  const { allowBigChanges, setMaterialMode, setBook, handleLessonTab } =
    useCourseEditing();
  const cantDelete = !allowBigChanges || isLimitedEdition;

  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | undefined>(
    book.chapters.at(0),
  );

  const handleAfterEdit = (book: Book, lesson: Lesson) => {
    const foundChapter = book.chapters.some(
      ({ id }) => id === selectedChapter?.id,
    );
    navigate(
      buildMaterialUrl({
        lessonId: lesson.id,
        bookType,
        bookId: book.id,
        slugCourseName: course?.slug,
        blockId,
        chapterId: foundChapter ? selectedChapter?.id : undefined,
      }),
    );
  };

  const handleEditingBook = async () => {
    const { book, lesson } = await onEditBook();
    handleAfterEdit(book, lesson);
  };
  const { t } = useTranslation('translation', {
    keyPrefix: 'editMaterialPage',
  });

  const requestDeleteBook = async () => {
    const slugCourseName = course?.slug;
    let currentLesson = lesson;
    if (isPulished(lesson.status) && slugCourseName) {
      currentLesson = await updateLesson(
        {
          lessonId: currentLesson.id,
          slugCourseName,
          blockId,
        },
        {},
      );
    }
    const lessonId = currentLesson.id;
    const bookId =
      bookType === 'STUDENT'
        ? currentLesson.book?.id
        : currentLesson.teacherBook;

    if (!bookId) {
      throw new Error('Book invalid');
    }
    await deleteBook({ bookType, lessonId, bookId });
  };

  const { isPending: isLoadingDeleteBook, mutate: onDeleteBook } = useMutation(
    requestDeleteBook,
    {
      onSuccess() {
        handleLessonTab({
          previousActiveLessonOrder: null,
          activeLessonOrder: null,
          activeTab: 'details',
        });
        setMaterialMode('default');
        setBook(null);
        alert.success(t('deleted'));
        updateLessons?.();
      },
      onError(error: any) {
        const apiError = new ApiError(error);
        alert.error(apiError.getErrorMessage());
      },
    },
  );

  const content = (chapter?: Chapter) => {
    if (chapter) {
      if (anOldChapter(chapter))
        return chapterFormatter(
          chapter.title,
          stringToStringArray(chapter.content ?? ''),
        );
      return chapter.content;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <ModalDisable
        testId="deleteBookModal"
        modalType="delete"
        visible={visibleModal}
        onClickConfirm={() => onDeleteBook()}
        onClickCancel={() => setVisibleModal(false)}
        translationString="modalRemoveBook"
        isRequesting={isLoadingDeleteBook}
      />
      <div className="flex gap-3.5">
        <div className="w-[70%]">
          <HtmlPreview
            className="select-none"
            html={content(selectedChapter) ?? ''}
            format="book"
          />
        </div>

        <Chapters
          courseSlug={course?.slug ?? ''}
          lessonId={lesson.id}
          book={book}
          selectedChapter={selectedChapter}
          chapters={book.chapters}
          onSelectChapter={setSelectedChapter}
        />
      </div>
      <ConditionalRenderer condition={!readOnly}>
        <div className="flex justify-between gap-4">
          <MainButton
            loading={isLoadingEditingBook}
            text={t('editMaterial')}
            icon={<PencilIcon />}
            onClick={handleEditingBook}
          />
          <PedWorkspaceGuard isBlocked={cantDelete} className="w-fit">
            <MainButton
              text={t('deleteMaterial')}
              icon={<TrashIcon />}
              onClick={() => setVisibleModal(true)}
              color="warning"
              disabled={cantDelete}
            />
          </PedWorkspaceGuard>
        </div>
      </ConditionalRenderer>
    </div>
  );
}
